import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { toast } from "react-toastify";
import { IEmailTemplate, IEmailTemplateBlock } from "../../types/api";

const templateFields = `
  name
  label
  blocks {
    key
    label
    value
    shared
  }
  custom_blocks {
    key
    label
    value
    shared
  }
  supports_custom_blocks
`;

// getEmailTemplate / updateEmailTemplate return the EmailTemplateResponse
// union, so they need the inline fragments.
const templateSelection = `
  ... on EmailTemplate {
    ${templateFields}
  }
  ... on Error {
    message
    code
  }
`;

/**
 * The member-facing subset used by booking information popovers. The backend
 * owns the allow-list, so this cannot be used to browse admin-only templates.
 */
export const getBookingInfoTemplates = async (): Promise<IEmailTemplate[]> => {
  const query = `
    query getBookingInfoTemplates {
      getBookingInfoTemplates {
        ${templateFields}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.getBookingInfoTemplates;
    if (!Array.isArray(response)) {
      toast.error("Kunde inte hämta bokningsinformationen");
      return [];
    }
    return response.filter(Boolean) as IEmailTemplate[];
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return [];
  }
};

export const getEmailTemplate = async (
  name: string
): Promise<IEmailTemplate | null> => {
  const query = `
    query getEmailTemplate($name: String!) {
      getEmailTemplate(name: $name) {
        ${templateSelection}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      variables: { name },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.getEmailTemplate;
    if (!response || "message" in response) {
      toast.error(response?.message ?? "Kunde inte hämta mall");
      return null;
    }
    return response as IEmailTemplate;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return null;
  }
};

export const updateEmailTemplate = async (
  name: string,
  blocks: Pick<IEmailTemplateBlock, "key" | "value">[]
): Promise<boolean> => {
  const query = `
    mutation updateEmailTemplate($input: UpdateEmailTemplateInput!) {
      updateEmailTemplate(input: $input) {
        ${templateSelection}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      variables: {
        input: {
          name,
          blocks: blocks.map((b) => ({ key: b.key, value: b.value })),
        },
      },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.updateEmailTemplate;
    if (!response || "message" in response) {
      toast.error(response?.message ?? "Kunde inte spara mall");
      return false;
    }
    toast.success("Mall sparad");
    return true;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return false;
  }
};

/** Every template, for the selector. */
export const listEmailTemplates = async (): Promise<IEmailTemplate[]> => {
  const query = `
    query listEmailTemplates {
      listEmailTemplates {
        ${templateFields}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.listEmailTemplates;
    if (!Array.isArray(response)) {
      toast.error("Kunde inte hämta mallar");
      return [];
    }
    // The resolver returns null for an unknown name, so drop empty entries.
    return response.filter(Boolean) as IEmailTemplate[];
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return [];
  }
};

/**
 * Add an empty custom paragraph to one template. Returns the template as the
 * backend now has it, so the caller can pick up the generated key.
 */
export const createEmailBlock = async (
  templateName: string,
  label: string
): Promise<IEmailTemplate | null> => {
  const query = `
    mutation createEmailBlock($template_name: String!, $label: String!) {
      createEmailBlock(template_name: $template_name, label: $label) {
        ${templateSelection}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      variables: { template_name: templateName, label },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.createEmailBlock;
    if (!response || "message" in response) {
      toast.error(response?.message ?? "Kunde inte lägga till stycke");
      return null;
    }
    return response as IEmailTemplate;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return null;
  }
};

export const deleteEmailBlock = async (
  templateName: string,
  key: string
): Promise<IEmailTemplate | null> => {
  const query = `
    mutation deleteEmailBlock($template_name: String!, $key: String!) {
      deleteEmailBlock(template_name: $template_name, key: $key) {
        ${templateSelection}
      }
    }
  `;
  try {
    const res = (await API.graphql({
      query,
      variables: { template_name: templateName, key },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;
    const response = res.data.deleteEmailBlock;
    if (!response || "message" in response) {
      toast.error(response?.message ?? "Kunde inte ta bort stycke");
      return null;
    }
    toast.success("Stycke borttaget");
    return response as IEmailTemplate;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    return null;
  }
};
